import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  Alert,
  Button,
  Input,
  Label,
  Modal,
  ModalFooter,
  Spinner,
  EmptyState,
} from "@/components/ui";
import { RegionSelect } from "@/components/common/RegionSelect";
import { QueryLoadError } from "@/components/common/QueryLoadError";
import { getApiErrorMessage } from "@/lib/errorMessages";
import { queryLoadFailure } from "@/lib/queryLoadFailure";
import { normalizeReaderSerialNumber } from "@/lib/readerSerialNumber";
import {
  readerPresetKeys,
  readerPresetService,
} from "@/services/readerPresetService";
import { readerService } from "@/services/readerService";
import { cn } from "@/lib/utils";
import { CheckCircle2, CreditCard } from "lucide-react";

interface CreateReaderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => Promise<void>;
  /** Prefill from the list region filter when set; otherwise empty (required). */
  preferredRegionId: string | null;
}

export function CreateReaderModal({
  isOpen,
  onClose,
  onCreated,
  preferredRegionId,
}: CreateReaderModalProps) {
  const { t } = useTranslation("readers");
  const { t: tCommon } = useTranslation("common");
  const { t: tErrors } = useTranslation("errors");
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [serialNumber, setSerialNumber] = useState("");
  const [regionId, setRegionId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const presetsQuery = useQuery({
    queryKey: readerPresetKeys.lists(),
    queryFn: () => readerPresetService.getAll(),
    enabled: isOpen,
  });
  const { data: presets = [] } = presetsQuery;
  const presetsLoadError = queryLoadFailure(
    presetsQuery,
    tErrors("loadReaderPresets"),
  );
  /** A single preset is preselected; otherwise the admin must pick one. */
  const presetId =
    selectedPresetId ?? (presets.length === 1 ? presets[0].id : null);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedPresetId(null);
    setSerialNumber("");
    setRegionId(preferredRegionId ?? "");
    setError(null);
  }, [isOpen, preferredRegionId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!presetId) {
      setError(t("createModal.presetRequired"));
      return;
    }
    if (!serialNumber.trim()) {
      setError(t("createModal.serialRequired"));
      return;
    }
    if (!regionId) {
      setError(t("createModal.regionRequired"));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await readerService.create({
        reader_preset_id: presetId,
        serial_number: normalizeReaderSerialNumber(serialNumber),
        region_id: regionId,
      });
      toast.success(t("page.readerCreated"));
      await onCreated();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, "generic"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      closeLabel={tCommon("close")}
      isOpen={isOpen}
      onClose={onClose}
      title={t("createModal.title")}
      size="lg"
      footer={
        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            {tCommon("cancel")}
          </Button>
          <Button
            type="submit"
            form="create-reader-form"
            disabled={isLoading}
            isLoading={isLoading}
          >
            {t("createModal.submit")}
          </Button>
        </ModalFooter>
      }
    >
      <form
        id="create-reader-form"
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        <div>
          <Label htmlFor="serialNumber">{t("createModal.serialLabel")}</Label>
          <Input
            id="serialNumber"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            placeholder={t("createModal.serialPlaceholder")}
            autoComplete="off"
            disabled={isLoading}
            required
            className="mt-2"
          />
        </div>

        <RegionSelect
          value={regionId}
          onChange={setRegionId}
          label={t("createModal.regionLabel")}
          placeholder={t("createModal.chooseRegion")}
          required
          disabled={isLoading}
        />

        <div className="space-y-2">
          <Label as="p">{t("createModal.presetSelectorTitle")}</Label>
          {presetsQuery.isLoading ? (
            <div className="flex justify-center py-4">
              <Spinner size="sm"  loadingLabel={tCommon("loading")} />
            </div>
          ) : presetsLoadError ? (
            <QueryLoadError failure={presetsLoadError} />
          ) : presets.length === 0 ? (
            <EmptyState
              size="sm"
              showIcon={false}
              title={t("createModal.presetsEmpty")}
              className="items-start px-0 py-0 text-left"
            />
          ) : (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
              {presets.map((preset) => (
                <button
                  type="button"
                  key={preset.id}
                  onClick={() => setSelectedPresetId(preset.id)}
                  aria-pressed={presetId === preset.id}
                  className={cn(
                    "relative flex items-center gap-3 rounded-lg border px-3.5 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                    presetId === preset.id
                      ? "border-primary-500 bg-primary-50 ring-1 ring-primary-500"
                      : "border-surface-200 bg-surface-0 hover:border-surface-300 hover:bg-surface-50",
                  )}
                >
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600 ring-1 ring-inset ring-violet-600/15"
                    aria-hidden
                  >
                    <CreditCard className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                  <span className="block pr-6 text-sm font-medium text-surface-900">{preset.brand}</span>
                  <span className="mt-0.5 block text-xs text-surface-500">
                    {t("createModal.presetCardSpecs", {
                      model: preset.model,
                      provider: preset.provider,
                      flow: preset.flow,
                    })}
                  </span>
                  </span>
                  {presetId === preset.id ? (
                    <CheckCircle2
                      className="absolute right-3 top-3 h-4 w-4 text-primary-600"
                      aria-hidden
                    />
                  ) : null}
                </button>
              ))}
            </div>
          )}
        </div>

        {error ? <Alert variant="error">{error}</Alert> : null}
      </form>
    </Modal>
  );
}
